import React, { useState, useCallback, useRef } from "react";
import FlowNodeEditorShell from "../FlowBuilder/FlowNodeEditorShell";
import { useFlowNodeEdit } from "../../hooks/useFlowNodeEdit";
import { getNodeEditorMeta } from "../../utils/flowNodeEditorMeta";
import { Handle, Position } from "reactflow";
import { Type, Pencil, Check, Variable, AlertTriangle } from "lucide-react";
import VariableDropdown from "../FlowBuilder/VariableDropdown";
import { getUnknownVariableNames } from "../../utils/flowVariableValidation";
import NodeActionButtons from "./NodeActionButtons";
import NodeEditorTabs from "./NodeEditorTabs";

// ─── Collapsed View ───────────────────────────────────
const CollapsedView = ({ data, onEdit }) => {
  const message = data.message || "";
  const displayText = message
    ? message.length > 100 ? message.substring(0, 100) + "..." : message
    : "";

  return (
    <div
      className="relative rounded-xl overflow-hidden"
      style={{ backgroundColor: "#EFF6FF", padding: "10px", minWidth: "240px", maxWidth: "300px" }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onEdit(); }}
        className="absolute top-2 right-2 z-10 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-md transition-all hover:shadow-lg"
        title="Edit message"
      >
        <Pencil size={12} className="text-blue-600" />
      </button>

      <div className="bg-white rounded-lg shadow-sm p-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="p-1.5 bg-blue-100 rounded-lg">
            <Type size={14} className="text-blue-600" />
          </div>
          <span className="text-[12px] font-bold text-blue-900">Text Message</span>
        </div>

        {displayText ? (
          <p className="text-[11px] text-gray-700 leading-tight whitespace-pre-wrap">
            {displayText}
          </p>
        ) : (
          <p className="text-[11px] text-gray-400 italic">No message set</p>
        )}
      </div>
    </div>
  );
};

// ─── Expanded Editor ──────────────────────────────────
const ExpandedEditor = ({ data, onSave, onCancel, flowVariables, onCreateVariable }) => {
  const [message, setMessage] = useState(data.message || "");
  const textareaRef = useRef(null);

  const unknownVars = getUnknownVariableNames(message, flowVariables);

  const insertVariable = (varName) => {
    if (!varName) return;
    const textarea = textareaRef.current;
    if (!textarea) {
      setMessage((prev) => prev + `{{${varName}}}`);
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = message.substring(0, start);
    const after = message.substring(end);
    const newMsg = before + `{{${varName}}}` + after;
    setMessage(newMsg);
    setTimeout(() => {
      const pos = start + `{{${varName}}}`.length;
      textarea.focus();
      textarea.setSelectionRange(pos, pos);
    }, 0);
  };

  return (
    <div
      className="nowheel"
      style={{ backgroundColor: "#EFF6FF", borderRadius: "12px", padding: "14px", minWidth: "300px", maxWidth: "380px" }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-blue-200 rounded-lg">
            <Type size={14} className="text-blue-700" />
          </div>
          <span className="text-[13px] font-bold text-blue-900">Text Message</span>
        </div>
        <div className="flex gap-1.5">
          <button
            onClick={() => onSave({ message })}
            className="px-3 py-1.5 text-[11px] font-bold bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1 shadow-sm"
          >
            <Check size={12} /> Save
          </button>
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-[11px] font-bold bg-white text-gray-600 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
          >
            Cancel
          </button>
        </div>
      </div>

      <NodeEditorTabs nodeType="textMessage">
      <div className="bg-white rounded-lg shadow-sm p-3 space-y-3">
        <div>
          <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1 block">Message</label>
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 text-[12px] text-gray-800 bg-[#F0F0F0] rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-blue-400 resize-none placeholder:text-gray-400"
            placeholder="Hello {{customer_name}}, your order is ready!"
          />

          {unknownVars.length > 0 && (
            <div className="flex items-start gap-1 mt-1 px-2 py-1 bg-amber-50 rounded text-[9px] text-amber-700">
              <AlertTriangle size={10} className="flex-shrink-0 mt-0.5" />
              <span>Unknown variable(s): <strong>{unknownVars.join(", ")}</strong>. Add them in Variables or use the list.</span>
            </div>
          )}
        </div>

        {/* Insert Variable */}
        <div>
          <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1 flex items-center gap-1">
            <Variable size={10} /> Insert Variable
          </label>
          <VariableDropdown
            value=""
            onChange={(name) => { if (name) insertVariable(name); }}
            flowVariables={flowVariables}
            allowCreate
            onCreateVariable={onCreateVariable}
            placeholder="Select variable to insert..."
          />
        </div>
      </div>

      </NodeEditorTabs>
      <div className="flex gap-2 mt-3">
        <button
          onClick={() => onSave({ message })}
          className="flex-1 py-2 text-[12px] font-bold bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-1.5 shadow-sm"
        >
          <Check size={14} /> Save
        </button>
        <button
          onClick={onCancel}
          className="flex-1 py-2 text-[12px] font-bold bg-white text-gray-600 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// ─── Main TextMessageNode ─────────────────────────────
const TextMessageNode = ({ id, data }) => {
  const { isEditing, openEditor, closeEditor } = useFlowNodeEdit(id, data);
  const editorMeta = getNodeEditorMeta("textMessage");

  const handleSave = useCallback(
    (formData) => {
      if (data.onNodeDataChange) {
        data.onNodeDataChange(id, { ...data, ...formData });
      }
      closeEditor();
    },
    [id, data, closeEditor]);

  return (
    <div className={`relative transition-all shadow-md`} style={{ borderRadius: "12px" }}>
      <NodeActionButtons id={id} data={data} />
      <Handle
        type="target"
        position={Position.Left}
        className="!w-3.5 !h-3.5 !bg-blue-500 !border-2 !border-white !shadow-sm"
        style={{ left: -7 }}
      />

      <CollapsedView data={data} onEdit={openEditor} />
      <FlowNodeEditorShell
        open={isEditing}
        onClose={closeEditor}
        title={data.label || editorMeta.label}
        subtitle={editorMeta.label}
        icon={editorMeta.icon}
        accent={editorMeta.accent}
      >
        <ExpandedEditor
          data={data}
          onSave={handleSave}
          onCancel={() => closeEditor()}
          flowVariables={data.flowVariables || []}
          onCreateVariable={data.onCreateVariable}
         presentation="fullscreen" onCancel={closeEditor} />
      </FlowNodeEditorShell>

      <Handle
        type="source"
        position={Position.Right}
        className="!w-3.5 !h-3.5 !bg-blue-500 !border-2 !border-white !shadow-sm"
        style={{ right: -7 }}
      />
    </div>
  );
};

export default TextMessageNode;
